/**
 * Reicuri AI Assistant Main Controller
 * Fully offline — uses ReicuriBrain (keyword engine) instead of any API.
 */

class ReicuriAssistant {
  constructor() {
    this.launcher = document.getElementById('reicuri-chat-launcher');
    this.chatWindow = document.getElementById('reicuri-chat-window');
    this.closeBtn = document.getElementById('reicuri-chat-close');
    this.resetBtn = document.getElementById('reicuri-chat-reset');
    this.messagesContainer = document.getElementById('reicuri-chat-messages');
    this.input = document.getElementById('reicuri-chat-input');
    this.submitBtn = document.getElementById('reicuri-chat-submit');
    this.quickReplies = document.querySelectorAll('.reicuri-qr-btn');

    this.isOpen = false;
    this.isTyping = false;

    this.init();
  }

  init() {
    this.bindEvents();
  }

  bindEvents() {
    this.launcher.addEventListener('click', () => this.toggleChat());
    if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.closeChat());
    if (this.resetBtn) this.resetBtn.addEventListener('click', () => this.resetChat());

    // Auto-resize textarea
    this.input.addEventListener('input', () => {
      this.input.style.height = 'auto';
      this.input.style.height = this.input.scrollHeight + 'px';
      this.validateInput();
    });

    // Enter to submit (Shift+Enter for new line)
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.handleSubmit();
      }
    });

    this.submitBtn.addEventListener('click', () => this.handleSubmit());

    // Quick Replies — use event delegation
    document.getElementById('reicuri-chat-quick-replies').addEventListener('click', (e) => {
      const btn = e.target.closest('.reicuri-qr-btn');
      if (btn) {
        this.sendUserMessage(btn.getAttribute('data-query'));
      }
    });
  }

  toggleChat() {
    this.isOpen ? this.closeChat() : this.openChat();
  }

  openChat() {
    this.isOpen = true;
    this.chatWindow.classList.remove('reicuri-hidden');
    this.chatWindow.setAttribute('aria-hidden', 'false');

    const openIcon = this.launcher.querySelector('.reicuri-launcher-icon');
    const closeIcon = this.launcher.querySelector('.reicuri-launcher-close-icon');
    if (openIcon) { openIcon.style.opacity = '0'; openIcon.style.transform = 'scale(0.5)'; }
    if (closeIcon) closeIcon.classList.remove('reicuri-hidden');

    setTimeout(() => this.input.focus(), 300);
    this.scrollToBottom();
  }

  closeChat() {
    this.isOpen = false;
    this.chatWindow.classList.add('reicuri-hidden');
    this.chatWindow.setAttribute('aria-hidden', 'true');

    const openIcon = this.launcher.querySelector('.reicuri-launcher-icon');
    const closeIcon = this.launcher.querySelector('.reicuri-launcher-close-icon');
    if (openIcon) { openIcon.style.opacity = '1'; openIcon.style.transform = 'scale(1)'; }
    if (closeIcon) closeIcon.classList.add('reicuri-hidden');
  }

  resetChat() {
    this.messagesContainer.innerHTML = `
      <div class="reicuri-message reicuri-message-ai">
        <div class="reicuri-message-bubble">
          <p>Hello! I'm your Reicuri AI Wellness Consultant. I can help you find the right supplements, track your order, or answer any questions.</p>
          <p>What brings you here today?</p>
        </div>
      </div>`;
    this.input.value = '';
    this.validateInput();
  }

  validateInput() {
    const val = this.input.value.trim();
    this.submitBtn.disabled = val.length === 0;
  }

  handleSubmit() {
    const text = this.input.value.trim();
    if (!text || this.isTyping) return;
    this.input.value = '';
    this.input.style.height = 'auto';
    this.validateInput();
    this.sendUserMessage(text);
  }

  sendUserMessage(text) {
    // Render user message
    this.appendMessage('user', text);

    // Show typing indicator
    this.showTypingIndicator();
    this.isTyping = true;

    // Simulate a small delay for the "thinking" feel
    setTimeout(() => {
      this.removeTypingIndicator();
      this.isTyping = false;

      // Get offline response from the Brain
      const response = window.ReicuriBrain.respond(text);

      // Render AI message
      const msgId = 'reicuri-msg-' + Date.now();
      this.appendMessage('assistant', response.text, msgId);

      // Inject product cards if any
      if (response.cards && response.cards.length > 0) {
        const bubble = document.getElementById(msgId);
        if (bubble) {
          response.cards.forEach(cardId => {
            bubble.insertAdjacentHTML('afterend', window.ReicuriBrain.formatProductCard(cardId));
          });
        }
      }

      // Inject MCQ options if any
      if (response.options && response.options.length > 0) {
        const bubble = document.getElementById(msgId);
        if (bubble) {
          const optionsDiv = document.createElement('div');
          optionsDiv.className = 'reicuri-chat-inline-options';
          optionsDiv.style.display = 'flex';
          optionsDiv.style.flexWrap = 'wrap';
          optionsDiv.style.gap = '8px';
          optionsDiv.style.marginTop = '12px';
          
          response.options.forEach(opt => {
            const btn = document.createElement('button');
            btn.className = 'reicuri-qr-btn';
            btn.innerText = opt.label;
            btn.onclick = () => {
               // Remove options after selection for a cleaner flow
               optionsDiv.remove();
               this.sendUserMessage(opt.query);
            };
            optionsDiv.appendChild(btn);
          });
          
          bubble.parentNode.appendChild(optionsDiv);
        }
      }

      this.scrollToBottom();
    }, 600 + Math.random() * 400); // Natural 600-1000ms delay
  }

  appendMessage(role, content, id = null) {
    const msgDiv = document.createElement('div');
    msgDiv.className = `reicuri-message reicuri-message-${role === 'user' ? 'user' : 'ai'}`;

    const bubble = document.createElement('div');
    bubble.className = 'reicuri-message-bubble';
    if (id) bubble.id = id;

    bubble.innerHTML = this.parseMarkdown(content);
    msgDiv.appendChild(bubble);
    this.messagesContainer.appendChild(msgDiv);
    this.scrollToBottom();
  }

  showTypingIndicator() {
    const msgDiv = document.createElement('div');
    msgDiv.id = 'reicuri-typing';
    msgDiv.className = 'reicuri-message reicuri-message-ai';
    msgDiv.innerHTML = `
      <div class="reicuri-message-bubble reicuri-typing-indicator">
        <div class="reicuri-typing-dot"></div>
        <div class="reicuri-typing-dot"></div>
        <div class="reicuri-typing-dot"></div>
      </div>`;
    this.messagesContainer.appendChild(msgDiv);
    this.scrollToBottom();
  }

  removeTypingIndicator() {
    const el = document.getElementById('reicuri-typing');
    if (el) el.remove();
  }

  scrollToBottom() {
    this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight;
  }

  parseMarkdown(text) {
    if (!text) return '';
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/_(.*?)_/g, '<em>$1</em>')
      .replace(/\n/g, '<br>');
  }
}

// Initialize
const initReicuri = () => {
  if (!window.ReicuriAI) {
    window.ReicuriAI = new ReicuriAssistant();
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initReicuri);
} else {
  initReicuri();
}
